import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticleLayoutProps {
  title: string;
  description: string;
  category: string;
  readTime: string;
  breadcrumbs: { label: string; href?: string }[];
  pageTitle: string;
  children: React.ReactNode;
  faqData?: { question: string; answer: string }[];
  articleSchema?: Record<string, unknown>;
}

export default function ArticleLayout({
  title,
  description,
  category,
  readTime,
  breadcrumbs,
  pageTitle,
  children,
  faqData,
  articleSchema,
}: ArticleLayoutProps) {
  useEffect(() => {
    document.title = pageTitle;
    window.scrollTo(0, 0);
  }, [pageTitle]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuyfinds.mom';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  return (
    <>
      <Navbar />
      <main className="pt-[72px]">
        <div className="w-full bg-background-50 border-b border-background-200/50">
          <div className="w-full px-6 md:px-10 py-3">
            <div className="max-w-[1280px] mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                {breadcrumbs.map((crumb, idx) => (
                  <span key={idx} className="flex items-center gap-2">
                    {idx > 0 && <i className="ri-arrow-right-s-line text-xs"></i>}
                    {crumb.href ? (
                      <Link to={crumb.href} className="hover:text-primary-500 transition-colors">{crumb.label}</Link>
                    ) : (
                      <span className="text-foreground-700 font-medium">{crumb.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <section className="w-full bg-accent-50/50">
          <div className="w-full px-6 md:px-10 py-16 md:py-24">
            <div className="max-w-[800px] mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-100/60 rounded-full mb-4">
                <span className="text-xs font-medium text-accent-700">{category}</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                {title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                {description}
              </p>
              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  Aktualizacja: Lipiec 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  KakobuyFinds
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="w-full px-6 md:px-10 py-12 md:py-16">
            <div className="max-w-[800px] mx-auto">
              <article className="prose-custom space-y-8 text-foreground-700 leading-relaxed">
                {children}

                <div className="bg-primary-50/70 rounded-2xl p-8 text-center my-10">
                  <h3 className="text-xl font-bold text-foreground-900 mb-2">
                    Gotowy, aby zacząć korzystać z Kakobuy Spreadsheet?
                  </h3>
                  <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                    Dołącz do tysięcy zadowolonych użytkowników i odkryj najlepsze Kakobuy Finds już dziś.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="group inline-flex items-center gap-2 px-6 py-3.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <span>Przejdź do Kakobuy Spreadsheet</span>
                      <span className="w-7 h-7 flex items-center justify-center bg-background-50/20 rounded-full group-hover:translate-x-0.5 transition-transform">
                        <i className="ri-arrow-right-line text-sm"></i>
                      </span>
                    </a>
                    <Link
                      to="/"
                      className="px-6 py-3.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      ← Powrót na stronę główną
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {articleSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              ...articleSchema,
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `${siteUrl}${currentPath}`,
              },
            }),
          }}
        />
      )}

      {faqData && faqData.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqData.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
              })),
            }),
          }}
        />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((b, idx) => ({
              '@type': 'ListItem',
              position: idx + 1,
              name: b.label,
              item: b.href ? `${siteUrl}${b.href}` : `${siteUrl}${currentPath}`,
            })),
          }),
        }}
      />
    </>
  );
}